'use client';

import '@vidstack/react/player/styles/base.css';

// React imports
import {
  MouseEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import Image from 'next/image';
import CaretIcon from 'components/icons/CaretIcon';

// Redux imports
import { useAppSelector } from 'lib/hooks';
import { RootState } from 'lib/store';
import { selectDisplayClosedCaptions } from 'slices/selectors';

// Vidstack imports
import {
  MediaPlayer,
  type MediaPlayerInstance,
  MediaProvider,
  useMediaRemote,
  useMediaState,
  TimeSlider,
  Captions,
} from '@vidstack/react';

// Local imports
import { usePlayer } from 'contexts/PlayerContext';
import DynamicText from './DynamicText';
import { useCaptionVisibility } from 'hooks/useCaptionVisibility';
import { useTextTrackMode } from 'hooks/useTextTrackMode';
import { useTextTracks } from 'hooks/useTextTracks';
import { isVideoSurfaceClick } from 'utils/videoSurfaceClick';

interface VideoPlayerProps {
  autoPlay?: boolean;
  disableDefaultControls?: boolean;
  dynamicText?: string;
  isAudio?: boolean;
  onError?: (error: Error) => void;
  onLoad?: () => void;
  onLoadStart?: () => void;
  poster?: string;
  source: null | string;
  track?: string;
  stillImage?: string;
  transcript?: string;
  onContinue?: () => void;
}

const VideoPlayer = ({
  autoPlay = true,
  dynamicText,
  isAudio,
  onLoad,
  onLoadStart,
  poster,
  source,
  track = '',
  stillImage,
  onContinue,
}: VideoPlayerProps) => {
  const playerRef = useRef<MediaPlayerInstance>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  const { volume, isMuted } = useAppSelector((state: RootState) => state.media);

  const { setPlayer } = usePlayer();

  useEffect(() => {
    if (playerRef) {
      setPlayer(playerRef);
    }
  }, [setPlayer, source]);

  const remote = useMediaRemote(playerRef);
  const isPaused = useMediaState('paused', playerRef);
  const [hasStarted, setHasStarted] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(false);
  const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const clearHideControlsTimeout = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
  }, []);

  // Clear any pending hide timer on unmount.
  useEffect(() => clearHideControlsTimeout, [clearHideControlsTimeout]);

  // Hide controls when the video first loads, only show controls when paused.
  useEffect(() => {
    if (!isPaused) queueMicrotask(() => setHasStarted(true));

    if (!hasStarted) return;

    if (isPaused) clearHideControlsTimeout();
    queueMicrotask(() => setControlsVisible(isPaused));
  }, [isPaused, hasStarted, clearHideControlsTimeout]);

  // Handle mouse movement: show controls, then hide again after 3s of inactivity while playing
  const handleMouseMove = useCallback(() => {
    setControlsVisible(true);
    clearHideControlsTimeout();
    if (!isPaused) {
      hideControlsTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    }
  }, [isPaused, clearHideControlsTimeout]);

  const showClosedCaptions = useAppSelector((state: RootState) =>
    selectDisplayClosedCaptions(state)
  );

  // Track caption visibility
  const captionsVisible = useCaptionVisibility(
    videoWrapperRef,
    source,
    showClosedCaptions
  );

  // Manage text track mode
  useTextTrackMode(playerRef, remote, showClosedCaptions, source, track);

  // Manage text tracks
  const textTracks = useMemo(() => {
    const tracks = [];
    if (track) {
      tracks.push({
        src: track,
        kind: 'captions' as const,
        label: 'Captions',
        language: 'en',
        type: 'vtt' as const,
      });
    }
    if (dynamicText) {
      tracks.push({
        src: dynamicText,
        kind: 'metadata' as const,
        label: 'Dynamic Text',
        language: 'en',
        type: 'vtt' as const,
        default: true,
      });
    }
    return tracks;
  }, [track, dynamicText]);

  useTextTracks(playerRef, remote, showClosedCaptions, source, textTracks);

  // Custom play/pause handler
  const handlePlayPause = useCallback(() => {
    if (remote) {
      if (isPaused) {
        remote.play();
      } else {
        remote.pause();
      }
    }
  }, [remote, isPaused]);

  const handleSurfaceClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (isVideoSurfaceClick(event.target)) {
        handlePlayPause();
      }
    },
    [handlePlayPause]
  );

  if (!source || !playerRef) {
    return null;
  }

  return (
    <div className="video-player">
      <div
        ref={videoWrapperRef}
        aria-describedby="VideoTranscript"
        className="video-wrapper"
        onClickCapture={handleSurfaceClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          if (!isPaused) {
            clearHideControlsTimeout();
            setControlsVisible(false);
          }
        }}
      >
        <MediaPlayer
          load="eager"
          preload="metadata"
          autoPlay={autoPlay}
          onLoad={onLoad}
          onLoadStart={onLoadStart}
          playsInline
          poster={poster || stillImage}
          ref={playerRef}
          src={source}
          onEnded={onContinue}
          volume={volume}
          muted={isMuted}
          viewType={isAudio ? 'audio' : 'video'}
        >
          <MediaProvider />

          {/* Custom Bottom Controls */}
          <div className="video-bottom-controls" data-visible={controlsVisible}>
            <TimeSlider.Root
              className="vds-time-slider vds-slider"
              aria-label="Video progress"
            >
              <TimeSlider.Track className="vds-slider-track" />
              <TimeSlider.TrackFill className="vds-slider-track-fill" />
              <TimeSlider.Progress className="vds-slider-progress" />
              <TimeSlider.Thumb className="vds-slider-thumb" />
            </TimeSlider.Root>

            {/* Control Buttons */}
            <div className="video-controls-container">
              {/* Play/Pause Button */}
              <button
                className="video-play-pause-btn"
                onClick={handlePlayPause}
                aria-label={isPaused ? 'Play' : 'Pause'}
              >
                <div className="play-pause-icon">
                  <Image
                    src={isPaused ? '/Play-circle.svg' : '/PauseCircle.svg'}
                    alt={isPaused ? 'Play' : 'Pause'}
                    width={32}
                    height={32}
                  />
                </div>
              </button>
              {/* Skip Button */}
              {onContinue && (
                <button className="video-skip-btn" onClick={onContinue}>
                  <span className="skip-text">Skip</span>
                  <CaretIcon direction="right" size="large" />
                </button>
              )}
            </div>
          </div>

          {showClosedCaptions && (
            <Captions
              className="vds-captions"
              style={{
                display: captionsVisible ? 'block' : 'none',
              }}
            />
          )}
          {dynamicText && <DynamicText playing={!isPaused} />}
        </MediaPlayer>
      </div>
    </div>
  );
};

export default VideoPlayer;
